import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { LangProvider } from "./i18n";
import { ThemeProvider } from "./theme";
import { AuthProvider, useAuth, homeFor } from "./api";
import Login from "./pages/Login";
import Landing from "./pages/Landing";
import Owner from "./pages/Owner";
import SchoolAdmin from "./pages/SchoolAdmin";
import Teacher from "./pages/Teacher";
import TeachingMode from "./pages/TeachingMode";
import Student from "./pages/Student";
import Topics from "./pages/Topics";
import Quiz from "./pages/Quiz";
import Journey from "./pages/Journey";
import Chat from "./pages/Chat";
import Tasks from "./pages/Tasks";
import Practice from "./pages/Practice";
import Leaderboard from "./pages/Leaderboard";
import Dictionary from "./pages/Dictionary";
import Competition from "./pages/Competition";
import { PreviewBanner } from "./components/PreviewBanner";

const Guard = ({ roles, children }) => {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-screen grid place-items-center text-slate-500">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={homeFor(user.role)} replace />;
  return children;
};

const Root = () => {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-screen grid place-items-center text-slate-500">Loading...</div>;
  return user ? <Navigate to={homeFor(user.role)} replace /> : <Landing />;
};

const LoginRoute = () => {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-screen grid place-items-center text-slate-500">Loading...</div>;
  return user ? <Navigate to={homeFor(user.role)} replace /> : <Login />;
};

export default function App() {
  return (
    <LangProvider>
      <ThemeProvider>
        <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-center" toastOptions={{
            style: { background: "#0A2540", color: "#fff", border: "none", fontWeight: 700 },
          }} />
          <Routes>
            <Route path="/" element={<Root />} />
            <Route path="/login" element={<LoginRoute />} />
            <Route path="/owner" element={<Guard roles={["owner"]}><Owner /></Guard>} />
            <Route path="/school" element={<Guard roles={["school_admin"]}><SchoolAdmin /></Guard>} />
            <Route path="/teacher" element={<Guard roles={["teacher"]}><Teacher /></Guard>} />
            <Route path="/teach/:topicId" element={<Guard roles={["teacher"]}><TeachingMode /></Guard>} />
            <Route path="/student" element={<Guard roles={["student"]}><Student /></Guard>} />
            <Route path="/student/journey" element={<Guard roles={["student"]}><Journey /></Guard>} />
            <Route path="/student/chat" element={<Guard roles={["student"]}><Chat /></Guard>} />
            <Route path="/student/topics" element={<Guard roles={["student"]}><Topics /></Guard>} />
            <Route path="/student/quiz" element={<Guard roles={["student"]}><Quiz /></Guard>} />
            <Route path="/student/tutor" element={<Navigate to="/student/chat" replace />} />
            <Route path="/student/games" element={<Navigate to="/student/journey" replace />} />
            <Route path="/student/tasks" element={<Guard roles={["student"]}><Tasks /></Guard>} />
            <Route path="/student/practice" element={<Guard roles={["student"]}><Practice /></Guard>} />
            <Route path="/student/leaderboard" element={<Guard roles={["student"]}><Leaderboard /></Guard>} />
            <Route path="/student/dictionary" element={<Guard roles={["student"]}><Dictionary /></Guard>} />
            <Route path="/student/competitions" element={<Guard roles={["student"]}><Competition /></Guard>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <PreviewBanner />
        </BrowserRouter>
      </AuthProvider>
      </ThemeProvider>
    </LangProvider>
  );
}
